import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, CalendarDays, Trophy, CheckCircle, Edit, FileDown, FileText, Clock, UserPlus, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { exportSchedulePDF, exportFITPSchedulePDF } from "@/lib/pdfExport";
import { buildPdfVenue } from "@/lib/pdfVenue";
import { computeGlobalLabelOverrides } from "@/lib/bracketUtils";

const ROUND_LABELS = {
  R256: "Turno Prel.", R128: "128-esimi", R64: "64-esimi", R32: "32-esimi",
  R16: "Sedicesimi", QF: "Quarti", SF: "Semifinale", F: "Finale",
};

// Looks up per-day court name, falls back to any date, then "Campo N"
const getCourtName = (courtNames, courtNum, dateStr) => {
  if (!courtNum) return "";
  const num = String(courtNum);
  if (courtNames && dateStr && courtNames[dateStr]) {
    const name = courtNames[dateStr][num] || courtNames[dateStr][courtNum];
    if (name && name.trim()) return name.trim();
  }
  if (courtNames) {
    for (const dayNames of Object.values(courtNames)) {
      if (typeof dayNames === "object" && dayNames) {
        const name = dayNames[num] || dayNames[courtNum];
        if (name && name.trim()) return name.trim();
      }
    }
  }
  return `Campo ${courtNum}`;
};

const SLOT_LABELS = { 0: "09:00-12:00", 1: "12:00-15:00", 2: "15:00-18:00", 3: "18:00-21:00" };

export default function TimetablePage() {
  const { id } = useParams();
  const [schedule, setSchedule] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [winnerId, setWinnerId] = useState("");
  const [score, setScore] = useState("");
  const [dialogMode, setDialogMode] = useState("result");
  const [statusPosition, setStatusPosition] = useState("player1");
  const [statusValue, setStatusValue] = useState("");
  const [catPlayers, setCatPlayers] = useState([]);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduleCourt, setRescheduleCourt] = useState("");
  const [rescheduleStartTime, setRescheduleStartTime] = useState("");
  const [rescheduleEndTime, setRescheduleEndTime] = useState("");
  const [rescheduling, setRescheduling] = useState(false);
  const [tournament, setTournament] = useState(null);
  const [venue, setVenue] = useState(null);
  const [filterCourt, setFilterCourt] = useState("all");

  const fetchSchedule = useCallback(async () => {
    try {
      const data = await api.get(`/tournaments/${id}/schedule`);
      setSchedule(data);
      const dates = Object.keys(data.by_date || {}).sort();
      if (dates.length > 0 && !selectedDate) setSelectedDate(dates[0]);
    } catch (e) {
      toast.error("Errore nel caricamento del calendario");
    } finally {
      setLoading(false);
    }
  }, [id, selectedDate]);

  useEffect(() => {
    fetchSchedule();
    api.get(`/tournaments/${id}`).then(setTournament).catch((e) => console.error("Failed to load tournament:", e));
    api.get(`/tournaments/${id}/venues`).then((v) => setVenue(v?.[0] || null)).catch((e) => console.error("Failed to load venues:", e));
  }, [id, fetchSchedule]);

  // Fetch full draw matches (including BYEs) for correct global label calculation
  const [drawMatches, setDrawMatches] = useState([]);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [scheduleNotes, setScheduleNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    setScheduleNotes(tournament?.pdf_schedule_notes || "");
  }, [tournament?.pdf_schedule_notes]);

  const saveScheduleNotes = async () => {
    if (!tournament) return;
    setSavingNotes(true);
    try {
      await api.put(`/tournaments/${tournament.tournament_id}`, { pdf_schedule_notes: scheduleNotes });
      setTournament({ ...tournament, pdf_schedule_notes: scheduleNotes });
      toast.success("Note salvate");
      setShowNotesDialog(false);
    } catch (e) {
      toast.error("Errore nel salvataggio delle note");
    } finally {
      setSavingNotes(false);
    }
  };
  useEffect(() => {
    if (!tournament?.categories?.length) return;
    Promise.all(tournament.categories.map(c =>
      api.get(`/tournaments/${id}/categories/${c.category_id}/draw`).then(d => d.matches || []).catch(() => [])
    )).then(results => setDrawMatches(results.flat()));
  }, [id, tournament]);

  const openResultDialog = (match, mode) => {
    // BYE matches cannot be edited at all
    if (match.status === "bye") return;
    setSelectedMatch(match);
    setWinnerId("");
    setScore("");
    setStatusPosition("player1");
    setStatusValue("");
    setRescheduleDate(match.scheduled_date || "");
    setRescheduleTime(match.scheduled_time || "");
    setRescheduleCourt(match.scheduled_court != null ? String(match.scheduled_court) : "");
    setRescheduleStartTime(match.start_time || "");
    setRescheduleEndTime(match.end_time || "");
    if (mode) {
      setDialogMode(mode);
    } else if (match.status === "completed") {
      // Completed matches can only be rescheduled (date/time change), not re-scored
      setDialogMode("reschedule");
    } else {
      const p1Real = match.player1_id && match.player1_id !== "BYE";
      const p2Real = match.player2_id && match.player2_id !== "BYE";
      setDialogMode(p1Real && p2Real ? "result" : "status");
    }
    // Fetch players for the match's category
    if (match.category_id) {
      api.get(`/tournaments/${id}/categories/${match.category_id}/players`)
        .then(setCatPlayers).catch(() => setCatPlayers([]));
    } else {
      setCatPlayers([]);
    }
  };

  const submitResult = async () => {
    if (!winnerId) { toast.error("Seleziona il vincitore"); return; }
    try {
      await api.put(`/tournaments/${id}/matches/${selectedMatch.match_id}`, { winner_id: winnerId, score });
      toast.success("Risultato registrato!");
      setSelectedMatch(null);
      fetchSchedule();
    } catch (e) { toast.error("Errore"); }
  };

  const submitStatus = async () => {
    if (!statusValue) { toast.error("Seleziona uno stato"); return; }
    try {
      await api.put(`/tournaments/${id}/matches/${selectedMatch.match_id}/swap`, {
        match_id: selectedMatch.match_id, position: statusPosition, new_player_id: statusValue,
      });
      toast.success(statusValue === "BYE" ? "BYE assegnato" : statusValue === "TBD" ? "Impostato come TBD" : "Giocatore assegnato!");
      setSelectedMatch(null);
      fetchSchedule();
    } catch (e) { toast.error("Errore"); }
  };

  const submitReschedule = async () => {
    if (!rescheduleDate && !rescheduleTime && !rescheduleCourt && !rescheduleStartTime && !rescheduleEndTime) {
      toast.error("Nessuna modifica inserita");
      return;
    }
    setRescheduling(true);
    try {
      const payload = {};
      if (rescheduleDate) payload.scheduled_date = rescheduleDate;
      if (rescheduleTime) payload.scheduled_time = rescheduleTime;
      if (rescheduleCourt) payload.scheduled_court = parseInt(rescheduleCourt, 10);
      if (rescheduleStartTime) payload.start_time = rescheduleStartTime;
      if (rescheduleEndTime) payload.end_time = rescheduleEndTime;
      await api.put(`/tournaments/${id}/matches/${selectedMatch.match_id}/reschedule`, payload);
      toast.success("Partita riprogrammata!");
      setSelectedMatch(null);
      fetchSchedule();
    } catch (e) { toast.error(e.message || "Errore nella riprogrammazione"); } finally { setRescheduling(false); }
  };

  const dates = useMemo(
    () => Object.keys(schedule?.by_date || {}).filter(d => d && d !== "unscheduled" && d !== "null" && !isNaN(new Date(d + "T00:00:00").getTime())).sort(),
    [schedule]
  );

  // Compute global label overrides per category using FULL draw (including BYEs)
  const labelOverrides = useMemo(() => {
    const source = drawMatches.length > 0 ? drawMatches : (schedule?.matches || []);
    const byCat = {};
    for (const m of source) {
      const cid = m.category_id || "_";
      if (!byCat[cid]) byCat[cid] = [];
      byCat[cid].push(m);
    }
    let result = {};
    for (const catMatches of Object.values(byCat)) {
      const overrides = computeGlobalLabelOverrides(catMatches);
      result = { ...result, ...overrides };
    }
    return result;
  }, [drawMatches, schedule]);
  const currentMatches = useMemo(
    () => {
      const raw = selectedDate ? (schedule?.by_date?.[selectedDate] || []) : [];
      let matches = raw.filter(m => m.status !== "bye" && m.player1_id !== "BYE" && m.player2_id !== "BYE");
      if (filterCourt !== "all") {
        matches = matches.filter(m => String(m.scheduled_court) === filterCourt);
      }
      return matches;
    },
    [schedule, selectedDate, filterCourt]
  );

  const availableCourts = useMemo(() => {
    const courts = new Set();
    for (const m of (schedule?.matches || [])) {
      if (m.scheduled_court != null) courts.add(String(m.scheduled_court));
    }
    return [...courts].sort((a, b) => Number(a) - Number(b));
  }, [schedule]);

  // Build a "filtered schedule" that mirrors the API response shape but contains
  // ONLY the matches matching the current filters (selected date + court). The
  // PDF exporters use this so the file reflects the on-screen selection.
  // IMPORTANT: rebuild `by_date` too — the PDF export iterates over `by_date`,
  // not `matches`, so we must filter both shape projections.
  const filteredSchedule = useMemo(() => {
    if (!schedule) return null;
    let matches = schedule.matches || [];
    if (selectedDate) matches = matches.filter(m => m.scheduled_date === selectedDate);
    if (filterCourt !== "all") matches = matches.filter(m => String(m.scheduled_court) === filterCourt);
    matches = matches.filter(m => m.status !== "bye" && m.player1_id !== "BYE" && m.player2_id !== "BYE");
    const byDate = {};
    for (const m of matches) {
      const key = m.scheduled_date || "unscheduled";
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(m);
    }
    return { ...schedule, matches, by_date: byDate };
  }, [schedule, selectedDate, filterCourt]);

  const matchesBySlot = useMemo(() => {
    const result = {};
    currentMatches.forEach((m) => {
      const slot = m.scheduled_slot ?? -1;
      if (!result[slot]) result[slot] = [];
      result[slot].push(m);
    });
    for (const slot in result) {
      result[slot].sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
    }
    return result;
  }, [currentMatches]);

  // Detect conflicts: same court + overlapping time on same date
  const conflictMatchIds = useMemo(() => {
    const ids = new Set();
    const matchDuration = tournament?.match_duration_minutes || 60;
    const allM = schedule?.matches || [];
    const scheduled = allM.filter(m => m.scheduled_date && m.scheduled_time && m.scheduled_court != null && m.status !== "bye");
    const byDateCourt = {};
    for (const m of scheduled) {
      const key = `${m.scheduled_date}|${m.scheduled_court}`;
      if (!byDateCourt[key]) byDateCourt[key] = [];
      byDateCourt[key].push(m);
    }
    for (const matches of Object.values(byDateCourt)) {
      if (matches.length < 2) continue;
      const sorted = [...matches].sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time));
      for (let i = 0; i < sorted.length; i++) {
        for (let j = i + 1; j < sorted.length; j++) {
          const [h1, m1] = sorted[i].scheduled_time.split(":").map(Number);
          const [h2, m2] = sorted[j].scheduled_time.split(":").map(Number);
          const t1 = h1 * 60 + m1;
          const t2 = h2 * 60 + m2;
          if (t1 === t2 || (t2 - t1) < matchDuration) {
            ids.add(sorted[i].match_id);
            ids.add(sorted[j].match_id);
          }
        }
      }
    }
    return ids;
  }, [schedule, tournament]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-green-700 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="timetable-page">
      <div className="flex items-center gap-4">
        <Link to={`/tournament/${id}`}>
          <Button variant="outline" size="sm" className="rounded-sm gap-1">
            <ArrowLeft className="h-4 w-4" /> Indietro
          </Button>
        </Link>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">Calendario</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900">
            Partite Programmate
          </h1>
        </div>
        {schedule?.matches?.length > 0 && tournament && (
          <div className="flex items-center gap-2 ml-auto">
            <Button
              data-testid="export-timetable-pdf-btn"
              variant="outline"
              size="sm"
              className="rounded-sm gap-1 text-green-700 border-green-200 hover:bg-green-50"
              onClick={() => buildPdfVenue(venue).then((v) => exportSchedulePDF(tournament, filteredSchedule || schedule, v, drawMatches, tournament.pdf_schedule_notes || ""))}
            >
              <FileDown className="h-4 w-4" /> Esporta PDF
            </Button>
            <Button
              data-testid="export-fitp-pdf-btn"
              variant="outline"
              size="sm"
              className="rounded-sm gap-1 text-blue-700 border-blue-200 hover:bg-blue-50"
              onClick={() => buildPdfVenue(venue).then((v) => exportFITPSchedulePDF(tournament, filteredSchedule || schedule, v, drawMatches, tournament.pdf_schedule_notes || ""))}
            >
              <FileDown className="h-4 w-4" /> PDF con tutti i loghi
            </Button>
            <Button
              data-testid="edit-timetable-notes-btn"
              variant="outline"
              size="sm"
              className="rounded-sm gap-1"
              onClick={() => setShowNotesDialog(true)}
            >
              <FileText className="h-4 w-4" /> Note PDF
            </Button>
          </div>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <Badge variant="secondary" className="text-xs rounded-sm">
            {schedule?.matches?.length || 0} partite
          </Badge>
          <Badge variant="secondary" className="text-xs rounded-sm">
            <CheckCircle className="h-3 w-3 mr-1" />
            {schedule?.matches?.filter(m => m.status === "completed").length || 0} completate
          </Badge>
        </div>
      </div>

      {dates.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-16 text-center">
            <CalendarDays className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessuna partita in calendario. Genera il tabellone e poi il calendario dalla pagina del torneo.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {dates.map((date) => {
              const dateObj = new Date(date + "T00:00:00");
              const dayLabel = dateObj.toLocaleDateString("it-IT", { weekday: "short" });
              const dayNum = dateObj.getDate();
              const monthLabel = dateObj.toLocaleDateString("it-IT", { month: "short" });
              const dayMatches = schedule?.by_date?.[date] || [];
              const completed = dayMatches.filter(m => m.status === "completed").length;

              return (
                <Button
                  key={date}
                  data-testid={`date-btn-${date}`}
                  variant={selectedDate === date ? "default" : "outline"}
                  onClick={() => setSelectedDate(date)}
                  className={`shrink-0 flex flex-col items-center h-auto py-2 px-4 rounded-sm ${
                    selectedDate === date ? "bg-green-700 hover:bg-green-800 text-white" : "hover:border-green-500"
                  }`}
                >
                  <span className="text-[10px] uppercase font-bold">{dayLabel}</span>
                  <span className="text-lg font-bold">{dayNum}</span>
                  <span className="text-[10px] uppercase">{monthLabel}</span>
                  <Badge variant={selectedDate === date ? "secondary" : "outline"} className="mt-1 text-[10px] h-4 px-1.5">
                    {completed}/{dayMatches.length}
                  </Badge>
                </Button>
              );
            })}
          </div>

          {availableCourts.length > 1 && (
            <div className="flex items-center gap-2">
              <Select data-testid="timetable-court-filter" value={filterCourt} onValueChange={setFilterCourt}>
                <SelectTrigger data-testid="timetable-court-filter-trigger" className="w-[160px] h-8 rounded-sm text-xs">
                  <SelectValue placeholder="Filtra campo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i campi</SelectItem>
                  {availableCourts.map(c => (
                    <SelectItem key={c} value={c}>{getCourtName(tournament?.court_names, c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {Object.keys(matchesBySlot).sort((a, b) => a - b).map((slotKey) => {
            const slotMatches = matchesBySlot[slotKey];
            const slotLabel = SLOT_LABELS[slotKey] || "Altro";

            return (
              <Card key={slotKey} className="bg-white">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg font-bold uppercase tracking-wide flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-700" />
                    {slotLabel}
                    <Badge variant="outline" className="ml-2 text-xs rounded-sm">{slotMatches.length} partite</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs uppercase tracking-wide">Orario</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide">Campo</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide">Rif.</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide">Categoria</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide">Turno</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide">Giocatore 1</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide">Giocatore 2</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide">Risultato</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {slotMatches.map((m) => {
                        const isCompleted = m.status === "completed";
                        const isBye = m.status === "bye";
                        const canEdit = !isCompleted && !isBye;
                        const canReschedule = !isBye;
                        const isConflict = conflictMatchIds.has(m.match_id);
                        return (
                          <TableRow
                            key={m.match_id}
                            data-testid={`schedule-row-${m.match_id}`}
                            className={`${isConflict ? "bg-red-50 border-l-4 border-l-red-500" : ""} ${canReschedule ? "cursor-pointer hover:bg-green-50 transition-colors" : ""}`}
                            onClick={() => canReschedule && openResultDialog(m)}
                          >
                            <TableCell className="font-medium tabular-nums">
                              {m.scheduled_time}
                              {isConflict && <span className="block text-[10px] font-bold text-red-600" data-testid={`conflict-label-${m.match_id}`}>CONFLITTO</span>}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="rounded-sm text-xs">{getCourtName(tournament?.court_names, m.scheduled_court, m.scheduled_date)}</Badge>
                            </TableCell>
                            <TableCell>
                              {m.match_label && (
                                <Badge data-testid={`schedule-label-${m.match_id}`} className="rounded-sm text-xs bg-green-100 text-green-800 hover:bg-green-100 font-bold">{labelOverrides[m.match_id] || m.match_label}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">{m.category_name}</TableCell>
                            <TableCell>
                              <Badge className="rounded-sm text-xs bg-slate-100 text-slate-700 hover:bg-slate-100">
                                {ROUND_LABELS[m.round] || m.round}
                              </Badge>
                            </TableCell>
                            <TableCell className={`text-sm font-medium ${
                              m.player1_id === "BYE" ? "text-slate-400 italic" :
                              !m.player1_id ? "text-amber-500 italic" :
                              isCompleted && m.winner_id === m.player1_id ? "text-green-700" : ""
                            }`}>
                              {m.player1_id === "BYE" ? "BYE" : m.player1_name || m.player1_tbd_label || "TBD"}
                              {m.player1_ranking && m.player1_id && m.player1_id !== "BYE" && !(m.player1_name || "").trim().endsWith(m.player1_ranking) && <span className="text-blue-600 font-semibold ml-1 text-xs">{m.player1_ranking}</span>}
                              {isCompleted && m.winner_id === m.player1_id && <Trophy className="h-3 w-3 inline ml-1 text-green-600" />}
                            </TableCell>
                            <TableCell className={`text-sm font-medium ${
                              m.player2_id === "BYE" ? "text-slate-400 italic" :
                              !m.player2_id ? "text-amber-500 italic" :
                              isCompleted && m.winner_id === m.player2_id ? "text-green-700" : ""
                            }`}>
                              {m.player2_id === "BYE" ? "BYE" : m.player2_name || m.player2_tbd_label || "TBD"}
                              {m.player2_ranking && m.player2_id && m.player2_id !== "BYE" && !(m.player2_name || "").trim().endsWith(m.player2_ranking) && <span className="text-blue-600 font-semibold ml-1 text-xs">{m.player2_ranking}</span>}
                              {isCompleted && m.winner_id === m.player2_id && <Trophy className="h-3 w-3 inline ml-1 text-green-600" />}
                            </TableCell>
                            <TableCell className="text-sm">
                              {isCompleted ? (
                                <span className="font-medium text-green-700">{m.score || "W.O."}</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {canEdit && (
                                <div className="flex items-center gap-1">
                                  <Button data-testid={`reschedule-btn-${m.match_id}`} variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-600 hover:text-amber-800" onClick={(e) => { e.stopPropagation(); openResultDialog(m, "reschedule"); }} title="Riprogramma">
                                    <Clock className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600 hover:text-green-800" onClick={(e) => { e.stopPropagation(); openResultDialog(m); }}>
                                    <Trophy className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              )}
                              {isCompleted && (
                                <div className="flex items-center gap-1">
                                  <Badge className="rounded-sm text-[10px] bg-green-100 text-green-800 hover:bg-green-100">OK</Badge>
                                  <Button data-testid={`reschedule-completed-btn-${m.match_id}`} variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-600 hover:text-amber-800" onClick={(e) => { e.stopPropagation(); openResultDialog(m, "reschedule"); }} title="Riprogramma">
                                    <Clock className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              )}
                              {isBye && (
                                <Badge className="rounded-sm text-[10px] bg-slate-100 text-slate-500 hover:bg-slate-100">BYE</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}

          {/* Unscheduled matches */}
          {(() => {
            const unsched = (schedule?.matches || []).filter(m => m.unscheduled_reason && !m.scheduled_date);
            if (unsched.length === 0) return null;
            return (
              <Card className="bg-white border-2 border-red-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg font-bold uppercase text-red-700">
                    Partite non programmate ({unsched.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {unsched.map((m) => (
                    <div key={m.match_id} className="bg-red-50 border border-red-200 rounded-sm p-2.5">
                      <div className="flex items-center gap-2">
                        <Badge className="rounded-sm text-[10px] bg-red-100 text-red-700 hover:bg-red-100 font-bold shrink-0">
                          {m.category_name || ""}
                        </Badge>
                        <Badge variant="outline" className="rounded-sm text-[10px] shrink-0">
                          {ROUND_LABELS[m.round] || m.round || ""}
                        </Badge>
                        <span className="text-sm font-medium">
                          {m.player1_name || "TBD"}
                          {m.player1_ranking && !(m.player1_name || "").trim().endsWith(m.player1_ranking) && <span className="text-blue-600 font-semibold ml-0.5 text-xs">{m.player1_ranking}</span>}
                          {" vs "}
                          {m.player2_name || "TBD"}
                          {m.player2_ranking && !(m.player2_name || "").trim().endsWith(m.player2_ranking) && <span className="text-blue-600 font-semibold ml-0.5 text-xs">{m.player2_ranking}</span>}
                        </span>
                      </div>
                      <p className="text-xs text-red-600 mt-1 pl-1">{m.unscheduled_reason}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })()}
        </>
      )}

      {/* Match Dialog - Same as Tabellone */}
      <Dialog open={!!selectedMatch} onOpenChange={() => setSelectedMatch(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">
              {dialogMode === "result" ? "Inserisci Risultato" : dialogMode === "reschedule" ? "Riprogramma Incontro" : "Modifica Giocatore"}
            </DialogTitle>
          </DialogHeader>
          {selectedMatch && (
            <div>
              <Tabs value={dialogMode} onValueChange={setDialogMode}>
                <TabsList className="w-full mb-4">
                  <TabsTrigger value="reschedule" className="flex-1 rounded-sm gap-1 text-xs">
                    <Clock className="h-3 w-3" /> Programmazione
                  </TabsTrigger>
                  <TabsTrigger value="result" className="flex-1 rounded-sm gap-1 text-xs" disabled={
                    selectedMatch.status === "completed" ||
                    !selectedMatch.player1_id || !selectedMatch.player2_id ||
                    selectedMatch.player1_id === "BYE" || selectedMatch.player2_id === "BYE"
                  }>
                    <Trophy className="h-3 w-3" /> Risultato
                  </TabsTrigger>
                  <TabsTrigger value="status" className="flex-1 rounded-sm gap-1 text-xs" disabled={selectedMatch.status === "completed"}>
                    <UserPlus className="h-3 w-3" /> Modifica
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="result">
                  <div className="space-y-4">
                    <div className="bg-slate-50 rounded-sm p-4 text-center">
                      <p className="text-sm font-medium">
                        <span className={!selectedMatch.player1_id ? "text-amber-500 italic" : selectedMatch.player1_id === "BYE" ? "text-slate-400 italic" : ""}>
                          {selectedMatch.player1_id === "BYE" ? "BYE" : selectedMatch.player1_name || selectedMatch.player1_tbd_label || "TBD"}
                        </span>
                        <span className="text-slate-400 mx-2">vs</span>
                        <span className={!selectedMatch.player2_id ? "text-amber-500 italic" : selectedMatch.player2_id === "BYE" ? "text-slate-400 italic" : ""}>
                          {selectedMatch.player2_id === "BYE" ? "BYE" : selectedMatch.player2_name || selectedMatch.player2_tbd_label || "TBD"}
                        </span>
                      </p>
                      <div className="flex items-center justify-center gap-2 mt-1">
                        <Badge className="rounded-sm text-xs bg-slate-100 text-slate-700 hover:bg-slate-100">{ROUND_LABELS[selectedMatch.round] || selectedMatch.round}</Badge>
                        <span className="text-xs text-slate-400">{selectedMatch.category_name}</span>
                      </div>
                      <Button data-testid="cal-result-goto-modifica-btn" variant="outline" size="sm" onClick={() => setDialogMode("status")} className="mt-2 text-xs rounded-sm gap-1 border-slate-300 hover:border-green-500 hover:bg-green-50">
                        <Edit className="h-3 w-3" /> Modifica
                      </Button>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase">Vincitore</Label>
                      <Select value={winnerId} onValueChange={setWinnerId}>
                        <SelectTrigger data-testid="timetable-winner-select" className="mt-1 rounded-sm"><SelectValue placeholder="Seleziona vincitore" /></SelectTrigger>
                        <SelectContent>
                          {selectedMatch.player1_id && selectedMatch.player1_id !== "BYE" && <SelectItem value={selectedMatch.player1_id}>{selectedMatch.player1_name}</SelectItem>}
                          {selectedMatch.player2_id && selectedMatch.player2_id !== "BYE" && <SelectItem value={selectedMatch.player2_id}>{selectedMatch.player2_name}</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase">Punteggio</Label>
                      <Input data-testid="timetable-score-input" value={score} onChange={(e) => setScore(e.target.value)} placeholder="es. 6-4 6-2" className="mt-1 rounded-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-bold uppercase">Ora Inizio <span className="text-slate-400 font-normal normal-case">(opzionale)</span></Label>
                        <Input data-testid="cal-result-start-time" type="time" value={rescheduleStartTime} onChange={(e) => setRescheduleStartTime(e.target.value)} className="mt-1 rounded-sm" />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Ora Fine <span className="text-slate-400 font-normal normal-case">(opzionale)</span></Label>
                        <Input data-testid="cal-result-end-time" type="time" value={rescheduleEndTime} onChange={(e) => setRescheduleEndTime(e.target.value)} className="mt-1 rounded-sm" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid="timetable-submit-result" onClick={submitResult} className="flex-1 bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">Salva Risultato</Button>
                      <Button data-testid="cal-open-reschedule-btn" variant="outline" onClick={() => setDialogMode("reschedule")} className="border-amber-500 text-amber-600 hover:bg-amber-50 font-bold uppercase rounded-sm text-xs">
                        <Clock className="h-3.5 w-3.5 mr-1" /> Riprogramma
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="reschedule">
                  <div className="space-y-3">
                    <div className="bg-slate-50 rounded-sm p-3 text-center text-sm">
                      <p className="font-medium">
                        {selectedMatch.player1_id === "BYE" ? "BYE" : selectedMatch.player1_name || selectedMatch.player1_tbd_label || "TBD"}
                        <span className="text-slate-400 mx-2">vs</span>
                        {selectedMatch.player2_id === "BYE" ? "BYE" : selectedMatch.player2_name || selectedMatch.player2_tbd_label || "TBD"}
                      </p>
                      <div className="flex items-center justify-center gap-2 mt-1">
                        <Badge className="rounded-sm text-xs bg-slate-100 text-slate-700 hover:bg-slate-100">{ROUND_LABELS[selectedMatch.round] || selectedMatch.round}</Badge>
                        <span className="text-xs text-slate-400">{selectedMatch.category_name}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-bold uppercase">Data</Label>
                        <Input data-testid="reschedule-date-input" type="date" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} className="mt-1 rounded-sm" />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Campo</Label>
                        <Input data-testid="reschedule-court-input" type="number" min="1" value={rescheduleCourt} onChange={(e) => setRescheduleCourt(e.target.value)} placeholder="es. 1" className="mt-1 rounded-sm" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase">Orario Programmato</Label>
                      <Input data-testid="reschedule-time-input" type="time" value={rescheduleTime} onChange={(e) => setRescheduleTime(e.target.value)} className="mt-1 rounded-sm" />
                    </div>
                    <Button data-testid="reschedule-submit-btn" onClick={submitReschedule} disabled={rescheduling} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase rounded-sm">
                      {rescheduling ? "Salvando..." : "Salva Programmazione"}
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="status">
                  <div className="space-y-4">
                    <div className="bg-slate-50 rounded-sm p-4 text-center text-sm">
                      <p>Partita: <strong>{selectedMatch.player1_id === "BYE" ? "BYE" : selectedMatch.player1_name || selectedMatch.player1_tbd_label || "TBD"}</strong> vs <strong>{selectedMatch.player2_id === "BYE" ? "BYE" : selectedMatch.player2_name || selectedMatch.player2_tbd_label || "TBD"}</strong></p>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase">Posizione</Label>
                      <Select value={statusPosition} onValueChange={setStatusPosition}>
                        <SelectTrigger data-testid="timetable-status-position" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="player1">{selectedMatch.player1_id === "BYE" ? "BYE" : selectedMatch.player1_name || selectedMatch.player1_tbd_label || "TBD"}</SelectItem>
                          <SelectItem value="player2">{selectedMatch.player2_id === "BYE" ? "BYE" : selectedMatch.player2_name || selectedMatch.player2_tbd_label || "TBD"}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase">Nuovo Giocatore</Label>
                      <Select value={statusValue} onValueChange={setStatusValue}>
                        <SelectTrigger data-testid="timetable-status-value" className="mt-1 rounded-sm"><SelectValue placeholder="Seleziona giocatore..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BYE" className="text-slate-400 italic">BYE (salta turno)</SelectItem>
                          <SelectItem value="TBD" className="text-amber-500">TBD (da definire)</SelectItem>
                          {catPlayers.map(p => <SelectItem key={p.player_id} value={p.player_id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button data-testid="timetable-submit-status" onClick={submitStatus} className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">
                      Assegna Giocatore
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold uppercase">Note sul PDF Calendario</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-xs text-slate-500">Le note verranno stampate in fondo a ogni pagina del PDF del calendario (sia quello standard sia quello con tutti i loghi).</p>
            <Textarea
              data-testid="timetable-notes-textarea"
              value={scheduleNotes}
              onChange={(e) => setScheduleNotes(e.target.value)}
              placeholder="Es. ‘Inizio incontri: ore 9:00 — Per info: 06 1234567‘ . Max ~4 righe."
              rows={5}
              className="rounded-sm resize-none"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowNotesDialog(false)} className="rounded-sm">Annulla</Button>
            <Button data-testid="save-timetable-notes-btn" onClick={saveScheduleNotes} disabled={savingNotes} className="bg-green-700 hover:bg-green-800 text-white rounded-sm">
              {savingNotes ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Salva note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
